import { CommonModule } from "@angular/common";
import {
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { NzIconModule } from "ng-zorro-antd/icon";
import { Subject, debounceTime, of, switchMap, takeUntil } from "rxjs";
import { mappingRoutes } from "src/app/core/mappingRoutes";
import { ComplaintService } from "src/app/services/complaint.service";

interface CommandResult {
  type: "plainte" | "route";
  label: string;
  hint?: string;
  icon: string;
  action: () => void;
}

interface NavShortcut {
  label: string;
  icon: string;
  path: string;
  keywords?: string[];
}

/**
 * Command palette globale (⌘K / Ctrl+K) — recherche multi-entités et navigation rapide.
 * Montée une seule fois dans `DashboardLayoutComponent`.
 */
@Component({
  selector: "app-command-palette",
  standalone: true,
  imports: [CommonModule, FormsModule, NzIconModule],
  templateUrl: "./command-palette.component.html",
  styleUrls: ["./command-palette.component.scss"],
})
export class CommandPaletteComponent implements OnInit, OnDestroy {
  isOpen = false;
  query = "";
  results: CommandResult[] = [];
  selectedIndex = 0;
  isLoading = false;

  @ViewChild("input") inputRef?: ElementRef<HTMLInputElement>;

  private destroy$ = new Subject<void>();
  private querySubject = new Subject<string>();

  /** Raccourcis de navigation (utilisés en mode "vide" et lors du filtrage textuel). */
  private readonly navShortcuts: NavShortcut[] = [
    {
      label: "Tableau de bord",
      icon: "dashboard",
      path: mappingRoutes.dashboard,
      keywords: ["accueil", "home", "stats"],
    },
    {
      label: "Liste des plaintes",
      icon: "unordered-list",
      path: mappingRoutes.plaintes.listing,
      keywords: ["plaintes", "complaints", "all"],
    },
    {
      label: "Nouvelle plainte",
      icon: "plus-circle",
      path: mappingRoutes.plaintes.create.home,
      keywords: ["créer", "ajouter", "new"],
    },
    {
      label: "Staffs",
      icon: "team",
      path: mappingRoutes.staffs.listing,
      keywords: ["staff", "membres"],
    },
    {
      label: "Administrateurs",
      icon: "user",
      path: mappingRoutes.administrateurs.listing,
      keywords: ["admin", "administrateur"],
    },
    {
      label: "Profils & permissions",
      icon: "safety-certificate",
      path: mappingRoutes.profiles.listing,
      keywords: ["profile", "permissions", "rbac"],
    },
    {
      label: "Référentiel — Catégories",
      icon: "tags",
      path: mappingRoutes.referential.categoriesPlaintes.listing,
      keywords: ["categorie", "category"],
    },
    {
      label: "Référentiel — Sensibilités",
      icon: "fire",
      path: mappingRoutes.referential.sensibilityPlaintes.listing,
      keywords: ["sensibilité", "sensitivity"],
    },
    {
      label: "Niveaux de traitement",
      icon: "ordered-list",
      path: mappingRoutes.levelsTraitementPlaintes.listing,
      keywords: ["level", "niveau", "traitement"],
    },
    {
      label: "Notifications",
      icon: "bell",
      path: mappingRoutes.notification,
      keywords: ["alertes"],
    },
    {
      label: "Mon profil",
      icon: "user",
      path: mappingRoutes.myProfile,
      keywords: ["compte"],
    },
  ];

  constructor(
    private router: Router,
    private complaintService: ComplaintService
  ) {}

  ngOnInit(): void {
    this.applyDefaultResults();
    this.querySubject
      .pipe(
        debounceTime(250),
        switchMap((q) => {
          const trimmed = (q || "").trim();
          if (trimmed.length < 2) {
            this.isLoading = false;
            this.applyFilteredNavOnly(trimmed);
            return of(null);
          }
          this.isLoading = true;
          return this.complaintService.search(trimmed);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res) => {
          this.isLoading = false;
          if (!res) return;
          this.applyMixedResults(res.data ?? []);
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener("document:keydown", ["$event"])
  handleKey(event: KeyboardEvent): void {
    // Cmd+K / Ctrl+K → toggle
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
      event.preventDefault();
      this.toggle();
      return;
    }

    if (!this.isOpen) return;

    switch (event.key) {
      case "Escape":
        event.preventDefault();
        this.close();
        break;
      case "ArrowDown":
        event.preventDefault();
        this.selectedIndex = Math.min(
          this.selectedIndex + 1,
          this.results.length - 1
        );
        break;
      case "ArrowUp":
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0);
        break;
      case "Enter":
        event.preventDefault();
        this.execute(this.results[this.selectedIndex]);
        break;
    }
  }

  toggle(): void {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  open(): void {
    this.isOpen = true;
    this.query = "";
    this.selectedIndex = 0;
    this.applyDefaultResults();
    setTimeout(() => this.inputRef?.nativeElement.focus(), 0);
  }

  close(): void {
    this.isOpen = false;
    this.query = "";
  }

  onQueryChange(value: string): void {
    this.query = value;
    this.selectedIndex = 0;
    this.querySubject.next(value);
  }

  execute(result: CommandResult | undefined): void {
    if (!result) return;
    result.action();
    this.close();
  }

  trackByIndex(_: number, _r: CommandResult): number {
    return _;
  }

  // ─── helpers ────────────────────────────────────────────────────────────

  private applyDefaultResults(): void {
    this.results = this.navShortcuts.map((n) => this.navToResult(n));
  }

  /** Filtrage des raccourcis quand la requête fait < 2 caractères. */
  private applyFilteredNavOnly(q: string): void {
    if (!q) {
      this.applyDefaultResults();
      return;
    }
    const lc = q.toLowerCase();
    this.results = this.navShortcuts
      .filter((n) => this.navMatches(n, lc))
      .map((n) => this.navToResult(n));
  }

  /** Mix résultats plaintes + raccourcis qui matchent la requête. */
  private applyMixedResults(plaintes: any[]): void {
    const lc = (this.query || "").toLowerCase();
    const plainteResults: CommandResult[] = (plaintes || []).map((p) => ({
      type: "plainte",
      label: p.trackingCode || `#${String(p.id).slice(0, 8)}`,
      hint: p.factsPlace || p.complaintCategory?.name || "—",
      icon: "alert",
      action: () =>
        this.router.navigate([mappingRoutes.plaintes.details, p.id]),
    }));
    const navResults = this.navShortcuts
      .filter((n) => this.navMatches(n, lc))
      .map((n) => this.navToResult(n));
    this.results = [...plainteResults, ...navResults];
  }

  private navMatches(n: NavShortcut, lc: string): boolean {
    if (n.label.toLowerCase().includes(lc)) return true;
    return (n.keywords || []).some((k) => k.toLowerCase().includes(lc));
  }

  private navToResult(n: NavShortcut): CommandResult {
    return {
      type: "route",
      label: n.label,
      hint: "Aller à",
      icon: n.icon,
      action: () => this.router.navigate([n.path]),
    };
  }
}
